import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import QuickEditImage from '@/components/quickedit/QuickEditImage';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import SectionPhotoCredit from '@/components/homepage/SectionPhotoCredit';

interface SeasonalSpotlightProps {
  section: any;
}

export default function SeasonalSpotlight({ section }: SeasonalSpotlightProps) {
  const content = section.content ?? {};
  const products: any[] = section.products ?? [];
  const headingId = `seasonal-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  return (
    <section aria-labelledby={headingId} className="bg-linen">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Editorial left column */}
          <div className="lg:col-span-1">
            <div className="overflow-hidden rounded-xl aspect-[4/3] mb-6">
              <QuickEditImage
                src={content.image_url}
                onSave={(url) => mutation.mutateAsync({ content: { ...section.content, image_url: url } })}
                label="Seasonal spotlight image"
                targetWidth={1200}
                targetHeight={900}
                specKey="seasonal_spotlight.image_url"
              >
                <ImageWithFallback
                  src={content.image_url}
                  alt={content.image_alt || section.title || 'Seasonal collection'}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <SectionPhotoCredit content={content} />
              </QuickEditImage>
            </div>
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900"
            >
              {isActive ? (
                <QuickEditInline
                  value={section.title || 'Seasonal Picks'}
                  onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                  label="Seasonal spotlight title"
                  as="span"
                  className="font-display text-3xl font-bold text-sage-900"
                />
              ) : (section.title || 'Seasonal Picks')}
            </h2>
            {section.subtitle && (
              <p className="mt-2 text-sage-600">
                {isActive ? (
                  <QuickEditInline
                    value={section.subtitle}
                    onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                    label="Seasonal spotlight subtitle"
                    as="span"
                    className="text-sage-600"
                  />
                ) : section.subtitle}
              </p>
            )}
            {content.description && (
              <p className="mt-3 text-sm text-sage-600 leading-relaxed">
                {isActive ? (
                  <QuickEditInline
                    value={content.description}
                    onSave={(val) => mutation.mutateAsync({ content: { ...section.content, description: val } })}
                    label="Seasonal spotlight description"
                    as="span"
                    className="text-sm text-sage-600 leading-relaxed"
                    multiline
                  />
                ) : content.description}
              </p>
            )}
            {content.cta_link && (
              <Link
                to={content.cta_link}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sage-700 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
              >
                {isActive ? (
                  <QuickEditInline
                    value={content.cta_text || 'Shop the collection'}
                    onSave={(val) => mutation.mutateAsync({ content: { ...section.content, cta_text: val } })}
                    label="Seasonal spotlight CTA text"
                    as="span"
                    className="text-sm font-medium text-sage-700"
                  />
                ) : (content.cta_text || 'Shop the collection')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </div>

          {/* Products grid */}
          {products.length > 0 && (
            <div className="lg:col-span-2">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6 list-none p-0">
                {products.map((product: any) => (
                  <li key={product.id}>
                    <ProductCard product={product} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
